import type { UserPublic } from '@repo/types/users';

import { useGetMyChildren } from '@repo/api/parent/useGetMyChildren';
import { cn } from '@repo/lib/cn';
import { isParent } from '@repo/lib/user-lvl/isParent';

import { useCredentials } from '@/store/auth-store';

interface EventChildrenProps {
  /**
   * Children this event belongs to, as `GET /api/parent/calendar` returns
   * them. Absent on every other calendar source, which is what keeps this
   * inert for students and teachers.
   */
  childIds?: number[];
  /** Initials fit inside an event chip; names suit the details dialog. */
  variant?: 'initials' | 'names';
  className?: string;
}

function initialsOf(child: UserPublic, nameLetters: number): string {
  return `${child.name.slice(0, nameLetters)}${child.surname.charAt(0)}`.toUpperCase();
}

/**
 * Says which of a parent's children an event belongs to.
 *
 * The merged parent calendar returns one row per meeting even when several
 * siblings attend it, so without this a parent cannot tell whose lesson they
 * are looking at.
 */
export function EventChildren(eventChildrenProps: EventChildrenProps) {
  const { childIds, variant = 'initials', className } = eventChildrenProps;
  const { accountType } = useCredentials();

  // One shared query across every event on screen; disabled entirely for
  // anyone who is not a parent.
  const { data: children = [] } = useGetMyChildren(isParent(accountType));

  if (!childIds?.length || !children.length) return null;

  const shown = childIds.map((childId) => children.find((child) => child.id === childId)).filter((child) => child !== undefined);

  if (!shown.length) return null;

  const initialsCollide = variant === 'initials' && new Set(shown.map((child) => initialsOf(child, 1))).size < shown.length;

  const labels = shown.map((child) => ({
    id: child.id,
    fullName: `${child.name} ${child.surname}`.trim(),
    label: variant === 'names' ? `${child.name} ${child.surname}`.trim() : initialsOf(child, initialsCollide ? 2 : 1),
  }));

  return (
    <span className={cn('inline-flex flex-wrap gap-1', className)}>
      {labels.map(({ id, label, fullName }) => (
        <span key={id} title={fullName} className={cn('rounded-sm px-1 font-medium', variant === 'initials' ? 'bg-black/10 text-[10px] dark:bg-white/15' : 'bg-accent text-accent-foreground text-xs')}>
          {label}
        </span>
      ))}
    </span>
  );
}
